import { useRecoilValue } from 'recoil';

import { RatesMarketRateDetails } from '@/components/modules/rates/market-rate-details';
import { RatesMarketRatePopout } from '@/components/modules/rates/market-rate-popout';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { MarketRateQueryModel } from '@/lib/models/rates/types';
import { getMarketRatesByRouteState } from '@/state/rates';
import { CargoOption } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { createMarketRatesKey, getXenetaAdditionalFields } from '@/utils/rates';

import { OptionSummaryCard } from '..';

interface MarketRatesSummaryCardProps {
  originId: string;
  destinationId: string;
  mode: SegmentModeType;
  cargo?: CargoOption;
  upgradeRequired?: boolean;
}

const MarketRatesSummaryCard = ({
  originId,
  destinationId,
  mode,
  cargo,
  upgradeRequired,
}: MarketRatesSummaryCardProps) => {
  const ratesAdditionalFields = cargo
    ? getXenetaAdditionalFields({
        containerSize: [cargo.containerSize!],
        cargoWeight: cargo.weight!,
        mode,
      })
    : undefined;

  const { fullKey, additionalFieldsKey } = createMarketRatesKey({
    ports: [{ pol: originId, pod: destinationId }],
    additionalFields: ratesAdditionalFields,
  });

  const rateQuery: MarketRateQueryModel = {
    id: fullKey,
    mode,
    ports: [{ pol: originId, pod: destinationId }],
    additionalFields: ratesAdditionalFields,
    label: `${originId} to ${destinationId}`,
  };

  const rates = useRecoilValue(getMarketRatesByRouteState({ pol: originId, pod: destinationId, additionalFieldsKey }));

  const { lastValue } = rates || {};
  const hasValue = !!lastValue?.value;

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger>
        <OptionSummaryCard isInteractive title="Market rate" className={!hasValue ? 'text-grey-400' : ''}>
          <RatesMarketRateDetails rates={rates} mode={mode} variant="sm" shouldShowChart={false} />
        </OptionSummaryCard>
      </HoverCardTrigger>
      <HoverCardContent align="end">
        <RatesMarketRatePopout rateQuery={rateQuery} mode={mode} rates={rates} upgradeRequired={upgradeRequired} />
      </HoverCardContent>
    </HoverCard>
  );
};

export { MarketRatesSummaryCard };
